import { Injectable, inject } from '@angular/core';
import { TranslationService } from './translation.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorMessagesService {
  private translationService = inject(TranslationService);

  // Mapping from old error codes to new translation keys
  private errorKeyMap: { [key: string]: string } = {
    OK: '',
    NETWORK: 'error.network',
    VALIDATION: 'error.validation',
    UNKNOWN: 'error.unknown',
    LOGIN_OK: 'error.login.ok',
    LOGIN_FAIL: 'error.login.fail',
    REG_FAIL: 'error.registration.fail',
    'FAIL-USER-REGISTRATION': 'error.user.exists',
    EMAIL_REQ_NOT_MET: 'error.email.requirements',
    CHECKOUT_OK: 'error.checkout.ok',
    CART_EMPTY: 'error.cart.empty',
    FILTER_SAVED: 'error.filter.saved',
    PASSWORD_MISMATCH: 'error.password.mismatch',
    REG_OK_CHECK_MAIL: 'error.registration.check.mail',
    EMAIL_MISMATCH: 'error.email.mismatch',
    ERR_PASSWORD_REQ_NOT_MET: 'error.password.requirements',
    PASSWORD_RESET_FAIL: 'error.password.reset.fail',
    REQUIRED_FIELDS: 'error.required.fields',
    FAIL_INVITATION_LINK: 'error.invitation.link.fail',
    SUCCESS_INVITATION_LINK: 'error.invitation.link.success',
    CHECKOUT_FAIL: 'error.checkout.fail',
    CHECKOUT_SUCCESS: 'error.checkout.success',
    ERR_UNKNOWN: 'error.unknown.short',
    ERR_EMPTY: 'error.empty',
    ERR_EMAIL: 'error.email.format',
    ERR_NUMBER: 'error.number',
    ERR_INTEGER: 'error.integer',
    ERR_FLOAT: 'error.float',
    ERR_MINLEN: 'error.minlen',
    ERR_MAXLEN: 'error.maxlen',
    ERR_LEN_RANGE: 'error.len.range',
    ERR_PASSWORD: 'error.password',
    ERR_REGEX_INVALID: 'error.regex',
    ERR_ALPHANUM_ONLY: 'error.job.name.requirements',
    JOB_CREATE_SUCCESS: 'error.job.create.success',
    JOB_CREATE_FAIL: 'error.job.create.fail',
    CONFIRM_DELETE_JOB: 'error.confirm.delete.job',
    CONFIRM_DELETE_USERS: 'error.confirm.delete.users',
    DELETE_USERS_SUCCESS: 'error.delete.users.success',
    JOB_DELETE_SUCCESS: 'error.job.delete.success',
    TESTCREDIT_LIMIT_REACHED: 'error.testcredit.limit',
    ERR_ALPHANUM_SPACE_ONLY: 'error.user.name.requirements',
    FAIL_CREATE_JOB: 'error.create.job.fail',
    SUCCESS_SETUP_INFO: 'error.setup.info.success',
    FAIL_SETUP_INFO_NOT_COMPLETE: 'error.setup.info.incomplete',
    FAIL_SETUP_INFO: 'error.setup.info.fail',
    LIMIT_REACHED: 'error.limit.reached',
  };

  getMessage(
    status: string,
    parameters?: { [key: string]: string | number }
  ): string {
    const translationKey = this.errorKeyMap[status] || 'error.unknown';
    
    // Convert parameters from {limit} to {{limit}} format for TranslationService
    const translatedParams = parameters ? 
      (() => {
        const result: { [key: string]: string } = {};
        Object.entries(parameters).forEach(([key, value]) => {
          result[key] = String(value);
        });
        return result;
      })() : undefined;

    return this.translationService.translate(translationKey, translatedParams);
  }

  getSuccessMessage(status: string): string {
    return this.getMessage(status);
  }

  getErrorMessage(status: string): string {
    return this.getMessage(status);
  }

  // Method to handle API error messages with field-specific validation
  getFieldErrorMessage(apiResponse: any): string {
    if (!apiResponse || !apiResponse.DATA || !apiResponse.DATA.message) {
      return this.getErrorMessage('UNKNOWN');
    }

    const messages = apiResponse.DATA.message;
    if (!Array.isArray(messages) || messages.length === 0) {
      return this.getErrorMessage('UNKNOWN');
    }

    // Handle field-specific validation errors
    const fieldErrors = messages.map(msg => {
      if (typeof msg === 'string') {
        // Parse field-specific error messages
        if (msg.includes('company PATTERN')) {
          return this.translationService.translate('error.field.company.pattern');
        }
        if (msg.includes('contact_name PATTERN')) {
          return this.translationService.translate('error.field.contact.pattern');
        }
        if (msg.includes('street PATTERN')) {
          return this.translationService.translate('error.field.street.pattern');
        }
        if (msg.includes('street_no PATTERN')) {
          return this.translationService.translate('error.field.streetno.pattern');
        }
        if (msg.includes('city PATTERN')) {
          return this.translationService.translate('error.field.city.pattern');
        }
        if (msg.includes('zip PATTERN')) {
          return this.translationService.translate('error.field.zip.pattern');
        }
        if (msg.includes('country PATTERN')) {
          return this.translationService.translate('error.field.country.pattern');
        }
        if (msg.includes('vat PATTERN')) {
          return this.translationService.translate('error.field.vat.pattern');
        }
        if (msg.includes('phone PATTERN')) {
          return this.translationService.translate('error.field.phone.pattern');
        }
        if (msg.includes('website PATTERN')) {
          return this.translationService.translate('error.field.website.pattern');
        }
        
        // Generic pattern error
        if (msg.includes('PATTERN')) {
          return this.translationService.translate('error.field.pattern.generic');
        }
        
        // Generic validation error
        if (msg.includes('FAIL:')) {
          return this.translationService.translate('error.field.validation');
        }
      }
      return msg;
    });

    return fieldErrors.join(' ');
  }
}
